import { useMemo, useState } from "react";
import {
  type DisplayableSpace,
  useSpaceDisplayName,
} from "../../hooks/useSpaceDisplayName";
import { makeUrl, navigate, routes, useCurrentUrl } from "../../lib/urls";
import { Button } from "../Button";
import { ListTable } from "../ListTable";
import { Modal } from "../Modal";
import { SearchField } from "../Settings/shared";
import { Sparkline } from "../Sparkline";
import { META_SEP } from "../MetaSeparator";
import { Text } from "../Text";
import { Tooltip } from "../Tooltip";
import { WorkflowBadge } from "../WorkflowBadge";
import {
  computeFailingWorkflows,
  computeFailureTrend,
  formatFailureDuration,
  sortFailingWorkflows,
  type FailingWorkflowSort,
  type MonitorWorkflowInput,
} from "./failingWorkflows";
import {
  DETAIL_MODAL_STYLE,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalHeaderRight,
  ModalTitle,
  SummaryCard,
} from "./MonitoringDetailCard";
import { ListSection, NameBody, NameCell } from "./monitoringList";
import { useMonitorSpaceWorkflows } from "./useMonitorSpaceWorkflows";
import { PanelSkeleton } from "./workflowsLayout";

function parseSort(value: string | undefined): FailingWorkflowSort {
  return value === "volume" ? "volume" : "duration";
}

const GHOST_FAILURE_TREND = [2, 4, 3, 6, 4, 7, 5, 9, 6, 8, 5, 7];

export function FailingWorkflowsPanel() {
  const { sort: sortParam } = useCurrentUrl(routes.monitoring.workflows);
  const sort = parseSort(sortParam);
  const { workflows: monitorWorkflows, result: workflowsQuery } =
    useMonitorSpaceWorkflows();
  const displayName = useSpaceDisplayName();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const workflowsWithRuns: readonly MonitorWorkflowInput[] = monitorWorkflows;

  const workflowById = useMemo(() => {
    const map = new Map<string, (typeof monitorWorkflows)[number]>();
    for (const workflow of monitorWorkflows) {
      map.set(workflow.id, workflow);
    }
    return map;
  }, [monitorWorkflows]);

  const spaceById = useMemo(() => {
    const map = new Map<string, DisplayableSpace>();
    for (const workflow of monitorWorkflows) {
      map.set(workflow.spaceId, workflow.space);
    }
    return map;
  }, [monitorWorkflows]);

  const runDailyStats = useMemo(
    () => monitorWorkflows.flatMap((w) => w.runDailyStats),
    [monitorWorkflows]
  );
  const failingWorkflows = useMemo(() => {
    const rows = computeFailingWorkflows(workflowsWithRuns, runDailyStats);
    return sortFailingWorkflows(rows, sort);
  }, [workflowsWithRuns, runDailyStats, sort]);

  const setSort = (next: FailingWorkflowSort) => {
    navigate(routes.monitoring.workflows, { sort: next }, { replace: true });
  };

  const count = failingWorkflows.length;

  const displayedWorkflows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return failingWorkflows;
    return failingWorkflows.filter((row) => row.name.toLowerCase().includes(q));
  }, [failingWorkflows, search]);

  const failureTrend = useMemo(
    () => computeFailureTrend(runDailyStats),
    [runDailyStats]
  );

  const failingSparkline =
    count > 0 ? (
      <Tooltip content={`Failed runs${META_SEP}last 30 days`}>
        <div style={{ display: "flex", flexShrink: 0 }}>
          <Sparkline values={failureTrend} color="var(--red-600)" />
        </div>
      </Tooltip>
    ) : (
      <div style={{ display: "flex", flexShrink: 0 }}>
        <Sparkline values={GHOST_FAILURE_TREND} color="var(--gray-400)" />
      </div>
    );

  if (workflowsQuery.type !== "complete") {
    return <PanelSkeleton />;
  }

  return (
    <>
      <SummaryCard
        title="Failing workflows"
        value={count}
        valueLabel="need attention"
        onOpen={
          count > 0
            ? () => {
                setSearch("");
                setOpen(true);
              }
            : undefined
        }
        accessory={failingSparkline}
      />

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        style={DETAIL_MODAL_STYLE}
      >
        <ModalHeader>
          <ModalTitle icon="traffic-cone" title="Failing workflows" />
          <ModalHeaderRight>
            <Button
              size="l"
              variant="secondary"
              icon="x"
              onClick={() => setOpen(false)}
            />
          </ModalHeaderRight>
        </ModalHeader>

        <ModalBody style={{ gap: 0 }}>
          <ListSection
            title="Failing workflows"
            description="Workflows with repeated failures on the live branch."
            action={
              <SearchField
                placeholder="Search workflows…"
                value={search}
                onChange={setSearch}
                width={280}
                autoFocus
              />
            }
          >
            <ListTable
              headers={{
                name: "Workflow",
                duration: "Failing for",
                volume: "Failed (7d)",
              }}
              columns={{ name: "flex", duration: "md", volume: "md" }}
              sortableColumns={["duration", "volume"]}
              sortColumn={sort}
              sortDirection="desc"
              onSort={(col) => setSort(col as FailingWorkflowSort)}
              emptyState={
                <Text variant="text-regular" color="var(--text-secondary)">
                  {search
                    ? "No workflows match your search."
                    : "Nothing failing."}
                </Text>
              }
              items={displayedWorkflows.map((row) => {
                const workflow = workflowById.get(row.workflowId);
                const space = spaceById.get(row.spaceId);
                return {
                  key: row.workflowId,
                  link: makeUrl(routes.space.workflow, {
                    spaceId: row.spaceId,
                    workflowId: row.workflowId,
                  }),
                  name: (hover: boolean) => (
                    <NameCell>
                      {workflow ? (
                        <WorkflowBadge
                          workflow={{
                            id: workflow.id,
                            badgeSeed: workflow.badgeSeed,
                            pausedAt: workflow.pausedAt,
                            branches: workflow.branches
                              ? [workflow.branches]
                              : [],
                          }}
                          hover={hover}
                        />
                      ) : null}
                      <NameBody>
                        <Text variant="text-regular-md" ellipsis>
                          {row.name}
                        </Text>
                        <Text
                          variant="text-small"
                          color="var(--text-secondary)"
                          ellipsis
                        >
                          {space ? displayName(space) : ""}
                        </Text>
                      </NameBody>
                    </NameCell>
                  ),
                  duration: (
                    <Text variant="text-small">
                      {formatFailureDuration(row.failureDurationMs)}
                    </Text>
                  ),
                  volume: (
                    <Text variant="text-small">
                      {row.failedRunCount} / {row.totalRunCount}
                    </Text>
                  ),
                };
              })}
            />
          </ListSection>
        </ModalBody>

        <ModalFooter>
          <Text variant="text-small" color="var(--text-secondary)">
            {count} need attention
          </Text>
          <Button size="l" variant="primary" onClick={() => setOpen(false)}>
            Done
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}
